import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";

export const BrandWordmark = ({ brand, className = "" }) =>
  brand.logo ? (
    <img src={brand.logo} alt={`${brand.name} logo`} className="h-full w-auto max-w-full object-contain object-left" loading="lazy" />
  ) : (
    <span className={`font-display tracking-tight ${className}`}>{brand.name}</span>
  );

export const BrandPortfolioCard = ({ brand, index }) => (
  <motion.article
    className="group relative flex flex-col bg-[var(--paper)]"
    initial={{ opacity: 0, y: 40 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, amount: 0.15 }}
    transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1], delay: (index % 4) * 0.08 }}
    data-testid={`brand-card-${brand.id}`}
  >
    <div className="flex h-full flex-col p-5 md:p-6">
      <div className="flex items-center justify-between font-mono-label text-xs text-[var(--ink-2)]">
        <span>{String(index + 1).padStart(2, "0")} / 08</span>
        <span className="h-2 w-2 rounded-full" style={{ background: brand.tone === "pink" ? "var(--rose)" : "var(--sky)" }} />
      </div>
      <div className="relative mt-5 aspect-[4/5] overflow-hidden rounded-t-[999px] bg-[var(--paper-2)]">
        <img
          src={brand.image}
          alt={brand.imageAlt}
          loading="lazy"
          width="600"
          height="750"
          className="h-full w-full object-cover transition-transform duration-[1.2s] ease-[cubic-bezier(.2,.7,.2,1)] group-hover:scale-[1.06]"
        />
        <div className="absolute inset-0 bg-ink/0 transition-colors duration-500 group-hover:bg-ink/10" />
      </div>
      <p className="eyebrow mt-6 !text-[0.68rem]">{brand.category}</p>
      <h3 className="mt-3 flex h-20 items-center text-[1.75rem] leading-tight">
        <BrandWordmark brand={brand} />
      </h3>
      <p className="mt-3 text-[0.95rem] leading-relaxed text-[var(--ink-2)]">{brand.description}</p>
      <div className="mt-auto flex flex-wrap items-center justify-between gap-x-4 gap-y-2 pt-6 text-sm font-semibold">
        <Link to={`/brands#${brand.id}`} className="inline-flex items-center gap-2 after:absolute after:inset-0 after:content-['']" data-testid={`brand-card-cta-${brand.id}`}>
          <span className="link-underline group-hover:[background-size:100%_1px]">Explore {brand.name}</span>
          <ArrowUpRight size={16} className="transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
        </Link>
        {brand.url && (
          <a
            href={brand.url}
            target="_blank"
            rel="noopener noreferrer"
            className="relative z-10 inline-flex items-center gap-1 text-xs font-medium text-[var(--ink-2)] transition-colors hover:text-[var(--sky-ink)]"
            aria-label={`Visit ${brand.name} website (opens in a new tab)`}
            data-testid={`brand-card-visit-${brand.id}`}
          >
            <span className="link-underline">Visit Website</span>
            <ArrowUpRight size={13} />
          </a>
        )}
      </div>
    </div>
  </motion.article>
);

export const BrandPortfolioGrid = ({ brands }) => (
  <div className="grid gap-px bg-[var(--line)] border hairline sm:grid-cols-2 lg:grid-cols-4" data-testid="brand-portfolio-grid">
    {brands.map((b, i) => (
      <BrandPortfolioCard key={b.id} brand={b} index={i} />
    ))}
  </div>
);
